/** Social profile URLs shown on public business / pro pages. */
export type SocialLinksFields = {
  website?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  xUrl?: string;
};

export type SocialPlatformId =
  | 'website'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'linkedin'
  | 'youtube'
  | 'x'
  | 'whatsapp';

export type SocialLinkDisplay = {
  id: SocialPlatformId;
  label: string;
  href: string;
};

const FIELD_BY_PLATFORM: Record<
  Exclude<SocialPlatformId, 'whatsapp'>,
  keyof SocialLinksFields
> = {
  website: 'website',
  instagram: 'instagramUrl',
  facebook: 'facebookUrl',
  tiktok: 'tiktokUrl',
  linkedin: 'linkedinUrl',
  youtube: 'youtubeUrl',
  x: 'xUrl',
};

function trim(raw: string | undefined | null): string {
  return (raw ?? '').trim();
}

function withHttps(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

/** Normalize a single social field for persistence. */
export function normalizeSocialField(
  platform: Exclude<SocialPlatformId, 'whatsapp'>,
  raw: string | undefined | null,
): string | undefined {
  const value = trim(raw);
  if (!value) return undefined;

  switch (platform) {
    case 'website':
      return withHttps(value.replace(/^\/\//, ''));
    case 'instagram': {
      if (value.startsWith('@')) {
        const handle = value.slice(1).replace(/^@/, '');
        return handle ? `https://instagram.com/${handle}` : undefined;
      }
      if (/^instagram\.com/i.test(value) || /^www\./i.test(value)) return withHttps(value);
      if (!value.includes('.') && !value.includes('/')) return `https://instagram.com/${value}`;
      return withHttps(value);
    }
    case 'facebook':
    case 'tiktok':
    case 'linkedin':
    case 'youtube':
    case 'x': {
      if (value.startsWith('@') && platform === 'x') {
        const handle = value.slice(1);
        return handle ? `https://x.com/${handle}` : undefined;
      }
      if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(value) || value.includes('/')) return withHttps(value);
      if (platform === 'x') return `https://x.com/${value}`;
      if (platform === 'tiktok') return `https://tiktok.com/@${value.replace(/^@/, '')}`;
      return withHttps(value);
    }
    default:
      return withHttps(value);
  }
}

export function normalizeSocialLinks(raw: SocialLinksFields): SocialLinksFields {
  const out: SocialLinksFields = {};
  for (const platform of Object.keys(FIELD_BY_PLATFORM) as Exclude<SocialPlatformId, 'whatsapp'>[]) {
    const key = FIELD_BY_PLATFORM[platform];
    const normalized = normalizeSocialField(platform, raw[key]);
    if (normalized) out[key] = normalized;
  }
  return out;
}

export function socialLinksFromVendor(
  vendor: SocialLinksFields & { phone?: string },
): SocialLinksFields {
  return {
    website: vendor.website,
    instagramUrl: vendor.instagramUrl,
    facebookUrl: vendor.facebookUrl,
    tiktokUrl: vendor.tiktokUrl,
    linkedinUrl: vendor.linkedinUrl,
    youtubeUrl: vendor.youtubeUrl,
    xUrl: vendor.xUrl,
  };
}

export function socialLinksFromPro(pro: SocialLinksFields): SocialLinksFields {
  return socialLinksFromVendor(pro);
}

function whatsappHref(phone: string): string | undefined {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return undefined;
  return `https://wa.me/${digits}`;
}

/** Resolved links for icon buttons (newest / most useful order). */
export function listSocialLinks(
  fields: SocialLinksFields,
  options?: { phone?: string },
): SocialLinkDisplay[] {
  const links: SocialLinkDisplay[] = [];
  const order: Exclude<SocialPlatformId, 'whatsapp'>[] = [
    'website',
    'instagram',
    'facebook',
    'tiktok',
    'youtube',
    'linkedin',
    'x',
  ];

  const labels: Record<Exclude<SocialPlatformId, 'whatsapp'>, string> = {
    website: 'Website',
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    x: 'X',
  };

  for (const id of order) {
    const key = FIELD_BY_PLATFORM[id];
    const href = trim(fields[key]);
    if (!href) continue;
    links.push({
      id,
      label: labels[id],
      href: id === 'website' ? withHttps(href) : href,
    });
  }

  const wa = options?.phone ? whatsappHref(options.phone) : undefined;
  if (wa) links.push({ id: 'whatsapp', label: 'WhatsApp', href: wa });

  return links;
}

export function hasSocialLinks(fields: SocialLinksFields, phone?: string): boolean {
  return listSocialLinks(fields, { phone }).length > 0;
}
